import React, { useEffect, useMemo, useState } from "react";
import { XIcon, CheckIcon } from "../icons";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

/**
 * Link ONE hardware item to MANY products in one pass.
 *
 * Edited from the hardware side on purpose. Attaching from the product side
 * means opening 242 switch covers one at a time; from here you search, tick,
 * and set the count once.
 *
 * Quantity is per PHYSICAL unit, not per listing. A 10-pack listing multiplies
 * by its own pack size when the stock is consumed, so it needs no row of its own.
 */
export default function LinkHardwareModal({ hardware, store, storeName, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [linked, setLinked] = useState({}); // productId -> qtyPerUnit already saved
  const [picked, setPicked] = useState({}); // productId -> true, this session's selection
  const [qty, setQty] = useState(2);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/products?store=${store}`).then((r) => r.json()),
      fetch(`${API_URL}/api/product-hardware?store=${store}&hardwareId=${hardware.id}`).then((r) => r.json()),
    ])
      .then(([prods, links]) => {
        if (!alive) return;
        const list = Array.isArray(prods) ? prods : prods.products || [];
        const map = {};
        (Array.isArray(links) ? links : []).forEach((l) => { map[l.productId] = l.qtyPerUnit; });
        setProducts(list);
        setLinked(map);
        setPicked(Object.keys(map).reduce((o, k) => ({ ...o, [k]: true }), {}));
        // Start from what is already there so "save" is not a surprise reset.
        const first = Object.values(map)[0];
        if (first) setQty(first);
        setLoading(false);
      })
      .catch(() => { if (alive) { setErr("Could not load products."); setLoading(false); } });
    return () => { alive = false; };
  }, [store, hardware.id]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? products.filter((p) =>
          `${p.sku || ""} ${p.name || ""}`.toLowerCase().includes(q))
      : products;
    // Already-linked products float to the top so you can see what is set
    // without hunting, then plain alphabetical by SKU.
    return [...list].sort((a, b) => {
      const la = linked[a.id] !== undefined ? 0 : 1;
      const lb = linked[b.id] !== undefined ? 0 : 1;
      if (la !== lb) return la - lb;
      return String(a.sku || a.name || "").localeCompare(String(b.sku || b.name || ""));
    });
  }, [products, search, linked]);

  const pickedIds = Object.keys(picked).filter((k) => picked[k]).map(Number);
  const toAdd = pickedIds.filter((id) => linked[id] === undefined || linked[id] !== Number(qty));
  const toRemove = Object.keys(linked).map(Number).filter((id) => !picked[id]);

  const toggleAllShown = () => {
    const allOn = shown.length > 0 && shown.every((p) => picked[p.id]);
    const next = { ...picked };
    shown.forEach((p) => { if (allOn) delete next[p.id]; else next[p.id] = true; });
    setPicked(next);
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      if (toAdd.length) {
        const r = await fetch(`${API_URL}/api/product-hardware/bulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ store, hardwareId: hardware.id, productIds: toAdd, qtyPerUnit: Number(qty) || 1 }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Link failed");
      }
      if (toRemove.length) {
        const r = await fetch(`${API_URL}/api/product-hardware/bulk-unlink`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ store, hardwareId: hardware.id, productIds: toRemove }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Unlink failed");
      }
      onSaved(pickedIds.length);
      onClose();
    } catch (e) {
      setErr(e.message || "Something went wrong.");
      setSaving(false);
    }
  };

  const cell = { ...S.td, padding: "7px 12px" };
  const dirty = toAdd.length > 0 || toRemove.length > 0;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,15,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div style={{ background: "#F4F2EC", border: "3px solid #0A0A0F", width: "min(900px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 20, fontWeight: 900, color: "#0A0A0F", textTransform: "uppercase", letterSpacing: "-0.01em" }}>
              What uses {hardware.name}?
            </div>
            <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 2 }}>
              {storeName} · {pickedIds.length} product{pickedIds.length !== 1 ? "s" : ""} selected
            </div>
          </div>
          <button onClick={onClose} disabled={saving} style={{ ...S.iconBtn, fontSize: 16 }}><XIcon /></button>
        </div>

        <div style={{ padding: "14px 20px", borderBottom: "2px solid #0A0A0F", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU or name, e.g. TEMPLE"
            autoFocus
            style={{ ...S.input, marginBottom: 0, flex: "1 1 260px" }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap" }}>
            How many per unit
            <input
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              style={{ ...S.input, marginBottom: 0, width: 78, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}
            />
          </label>
          <button onClick={toggleAllShown} style={{ ...S.clearBtn, whiteSpace: "nowrap" }}>
            {shown.length > 0 && shown.every((p) => picked[p.id]) ? "Untick shown" : `Tick all ${shown.length}`}
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading && <div style={{ padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>Loading products…</div>}
          {!loading && shown.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>
              {search ? `No product matches "${search}".` : "No products in this store yet."}
            </div>
          )}
          {!loading && shown.length > 0 && (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: 46 }} />
                  <th style={{ ...S.th, width: "26%" }}>SKU</th>
                  <th style={S.th}>Product</th>
                  <th style={{ ...S.th, width: 90, textAlign: "center" }}>Now</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => {
                  const on = !!picked[p.id];
                  const already = linked[p.id];
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setPicked((s) => ({ ...s, [p.id]: !s[p.id] }))}
                      style={{ cursor: "pointer", background: on ? "rgba(0,51,255,0.07)" : undefined }}
                    >
                      <td style={{ ...cell, textAlign: "center" }}>
                        <span style={{
                          display: "inline-flex", width: 20, height: 20, border: "2px solid #0A0A0F",
                          alignItems: "center", justifyContent: "center",
                          background: on ? "#0033FF" : "transparent", color: "#fff",
                        }}>
                          {on ? <CheckIcon /> : null}
                        </span>
                      </td>
                      <td style={{ ...cell, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{p.sku || "—"}</td>
                      <td style={{ ...cell, fontSize: 13 }}>{p.name}</td>
                      <td style={{ ...cell, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: already !== undefined ? "#0033FF" : "rgba(10,10,15,0.35)" }}>
                        {already !== undefined ? `x${already}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: "14px 20px", borderTop: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: err ? "#C2410C" : "rgba(10,10,15,0.58)" }}>
            {err
              ? err
              : dirty
                ? `${toAdd.length} to set at x${qty}${toRemove.length ? `, ${toRemove.length} to unlink` : ""}`
                : "No changes"}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} disabled={saving} style={S.clearBtn}>Cancel</button>
            <button onClick={save} disabled={saving || !dirty} style={{ ...S.addBtn, opacity: saving || !dirty ? 0.5 : 1 }}>
              {saving ? "Saving…" : "Save links"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
